import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import ExchangeIcon from 'shells/logos/exchange.svg';
import GoogleLogoSvg from 'shells/logos/google-g.svg';
import {MarketingTitle} from 'shells/typography/marketing-title';

import type {
    TaskSync as TaskSyncFragment,
    setGoogleTaskSyncIsEnabled as setGoogleTaskSyncIsEnabledMutation,
    setGoogleTaskSyncIsEnabledVariables as setGoogleTaskSyncIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {InitialButton} from '../integrations/initial-button';
import {SetExchangeEmail} from '../shared/set-exchange-email';
import {WidgetExchange} from '../shared/widget-exchange';

import SET_GOOGLE_TASK_SYNC_IS_ENABLED from './graphql/mutations/set-google-task-sync-is-enabled.graphql';
import {TaskWidgetGoogle} from './tasks-widget-google';
import {updateCacheFromMutation} from './update-cache-from-mutation';

import styles from './tasks-sync-settings.module.css';

const exchangeLogo = <ExchangeIcon width={24} />;
const googleLogo = <GoogleLogoSvg width={24} />;

type Props = {
    tasksSync: TaskSyncFragment | null | undefined;
    refetchTaskSync: () => Promise<any>;
    errorMessages: string[];
    setErrorMessages: (arg1: string[]) => void;
};

class GoogleEnableMutation extends Mutation<
    setGoogleTaskSyncIsEnabledMutation,
    setGoogleTaskSyncIsEnabledMutationVariables
> {}

export function TasksSyncSettings(props: Props) {
    const [enablingExchange, setEnablingExchange] = React.useState<boolean>(false);

    if (enablingExchange) {
        return (
            <SetExchangeEmail
                onCancel={() => setEnablingExchange(false)}
                onSaveSuccess={() => {
                    props.refetchTaskSync().then(() => {
                        setEnablingExchange(false);
                    });
                }}
                onSaveError={(errMsg) => {
                    props.setErrorMessages(props.errorMessages.concat(errMsg));
                }}
            />
        );
    }

    const {tasksSync} = props;
    if (!tasksSync) {
        return (
            <React.Fragment>
                <MarketingTitle as='h3'>
                    View and complete your Nutshell tasks in Google
                    {NutClientConfig.integrations.exchangeEnabled ? ' or Exchange' : undefined}
                </MarketingTitle>
                <div className={styles['widget-container']}>
                    <GoogleEnableMutation
                        mutation={SET_GOOGLE_TASK_SYNC_IS_ENABLED}
                        update={updateCacheFromMutation}
                    >
                        {(enableGoogle) => (
                            <InitialButton
                                initialText='Connect to my Google tasks'
                                authCheckUrl='/google/has-auth'
                                windowFeatures={{
                                    toolbar: 'no',
                                    location: 'no',
                                    width: 600,
                                    height: 850,
                                }}
                                logo={googleLogo}
                                enable={enableGoogle}
                                onAuthStart={() => {
                                    props.setErrorMessages([]);
                                }}
                                onAuthSuccess={() => {
                                    return props.refetchTaskSync();
                                }}
                                onAuthError={(errMsgs) => {
                                    props.setErrorMessages(errMsgs.concat(props.errorMessages));
                                }}
                            />
                        )}
                    </GoogleEnableMutation>
                    {NutClientConfig.integrations.exchangeEnabled ? (
                        <IntegrationWidget
                            status='initial'
                            account={null}
                            initialText='Connect to my Exchange tasks'
                            logo={exchangeLogo}
                            onConnectClick={() => {
                                setEnablingExchange(true);
                            }}
                            onDisconnectClick={() => {}}
                        />
                    ) : undefined}
                </div>
            </React.Fragment>
        );
    }

    const {isEnabled, username, externalService, syncErrors} = tasksSync;

    if (externalService === 'GOOGLE' || externalService === 'GOOGLE_APPS') {
        return (
            <TaskWidgetGoogle
                isEnabled={isEnabled}
                errors={syncErrors || []}
                username={username}
                onAuthStart={() => {
                    props.setErrorMessages([]);
                }}
                onAuthSuccess={() => {
                    return props.refetchTaskSync();
                }}
                onAuthError={(errMsg) => {
                    props.setErrorMessages(props.errorMessages.concat(errMsg));
                }}
                externalService={externalService}
            />
        );
    }

    if (externalService === 'EXCHANGE') {
        return (
            <WidgetExchange
                isEnabled={isEnabled}
                // @ts-expect-error
                errors={syncErrors || []}
                username={username}
                onAuthStart={() => {
                    props.setErrorMessages([]);
                }}
                onAuthSuccess={() => {
                    return props.refetchTaskSync();
                }}
                onAuthError={(errMsg) => {
                    props.setErrorMessages(props.errorMessages.concat(errMsg));
                }}
            />
        );
    }

    return null;
}
